import { computed, toRefs } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { type ContactType } from "shared/composables/useContact";
import { prettyNumber } from "shared/helpers/number";
import { type Nullable } from "shared/types";

export type OutreachContactStatKey = "avgEngagement" | "topReaction";

interface OutreachContactSocialStatsProps {
  contact: ContactType;
}

export interface OutreachContactStatProps extends OutreachContactSocialStatsProps {
  suffix?: string;
  type: OutreachContactStatKey;
}

export interface OutreachContactStat {
  text: string;
  tooltip: string;
  value: Nullable<string | number>;
}

export function useOutreachContactSocialStats(
  props: OutreachContactSocialStatsProps
) {
  const { contact } = toRefs(props);

  const avgTotalEngagement = computed(() => {
    if ("average_total_engagements" in contact.value)
      return contact.value.average_total_engagements;

    return null;
  });

  const topReaction = computed(() => {
    if ("dominant_facebook_reaction" in contact.value)
      return contact.value.dominant_facebook_reaction;

    return null;
  });

  const hasStats = computed(
    () => avgTotalEngagement.value !== null || topReaction.value !== null
  );

  const stats = computed<Record<OutreachContactStatKey, OutreachContactStat>>(
    () => ({
      avgEngagement: {
        text: getLocaleText("outreach_contact_social_stats.avg_engagement"),
        tooltip: getLocaleText(
          "outreach_contact_social_stats.avg_engagement_tooltip"
        ),
        value: prettyNumber(avgTotalEngagement.value, "suffix"),
      },
      topReaction: {
        text: getLocaleText("outreach_contact_social_stats.top_reaction"),
        tooltip: getLocaleText(
          "outreach_contact_social_stats.top_reaction_tooltip"
        ),
        value: topReaction.value,
      },
    })
  );

  return {
    hasStats,
    stats,
  };
}

export default useOutreachContactSocialStats;
